/**
 * Token Refresh Diagnostics Utility
 *
 * Use this to debug token refresh issues.
 * Call `tokenDiagnostics.enable()` in your app to see detailed logs.
 */

export class TokenDiagnostics {
  private enabled = false

  enable() {
    this.enabled = true
    this.log("Token diagnostics enabled")
    this.checkCurrentTokens()
  }

  disable() {
    this.enabled = false
  }

  log(...args: any[]) {
    if (this.enabled) {
      console.log("[Token Diagnostics]", ...args)
    }
  }

  error(...args: any[]) {
    if (this.enabled) {
      console.error("[Token Diagnostics]", ...args)
    }
  }

  checkCurrentTokens() {
    const accessToken = sessionStorage.getItem("access_token")
    const refreshToken = sessionStorage.getItem("refresh_token")

    this.log("Current tokens:", {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenLength: accessToken?.length,
      refreshTokenLength: refreshToken?.length,
    })

    if (accessToken) {
      try {
        const payload = this.decodeJWT(accessToken)
        this.log("Access token payload:", payload)

        if (payload.exp) {
          const expiryDate = new Date(payload.exp * 1000)
          const now = new Date()
          const isExpired = expiryDate < now
          const timeUntilExpiry = expiryDate.getTime() - now.getTime()

          this.log("Access token expiry:", {
            expiryDate: expiryDate.toISOString(),
            isExpired,
            timeUntilExpiryMs: timeUntilExpiry,
            timeUntilExpiryMin: Math.floor(timeUntilExpiry / 60000),
          })
        }
      } catch (error) {
        this.error("Failed to decode access token:", error)
      }
    }
  }

  decodeJWT(token: string): any {
    try {
      const base64Url = token.split(".")[1]
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      throw new Error("Invalid JWT token")
    }
  }

  /**
   * Check if a JWT token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeJWT(token)
      if (!payload.exp) return false

      const expiryDate = new Date(payload.exp * 1000)
      const now = new Date()
      return expiryDate < now
    } catch (error) {
      return true // Treat invalid tokens as expired
    }
  }

  async testRefreshEndpoint(updateTokens: boolean = true) {
    const refreshToken = sessionStorage.getItem("refresh_token")
    if (!refreshToken) {
      this.error("No refresh token available to test")
      return null
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001/v1"
    this.log("Testing refresh endpoint:", `${baseUrl}/auth/refresh/`)

    try {
      const response = await fetch(`${baseUrl}/auth/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      this.log("Refresh endpoint response:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      })

      const data = await response.json()
      this.log("Refresh endpoint data:", data)

      if (response.ok) {
        this.log("✅ Refresh endpoint is working correctly")

        // Update localStorage with new tokens (token rotation support)
        if (updateTokens && data.access_token) {
          this.log("📝 Updating tokens in localStorage (token rotation)")
          sessionStorage.setItem("access_token", data.access_token)

          if (data.refresh_token) {
            sessionStorage.setItem("refresh_token", data.refresh_token)
            this.log("   → New refresh token stored")
          }

          this.log("   → Tokens updated successfully")
        } else if (!updateTokens) {
          this.log("⚠️  Note: Tokens NOT updated in localStorage (updateTokens=false)")
          this.log("   → Old refresh token is now invalid due to token rotation")
          this.log("   → Next test will fail unless you pass updateTokens=true")
        }

        return data
      } else {
        this.error("❌ Refresh endpoint failed:", data)

        // Check if it's a token rotation issue
        if (response.status === 400 && data.detail?.toLowerCase().includes("invalid")) {
          this.log("💡 Tip: This might be due to token rotation.")
          this.log("   The refresh token was already used and invalidated.")
          this.log("   This is normal security behavior.")
        }

        return null
      }
    } catch (error) {
      this.error("❌ Failed to call refresh endpoint:", error)
      return null
    }
  }

  simulateExpiredToken() {
    this.log("Simulating expired token scenario...")
    // You can manually set an expired token for testing
    const expiredToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE2MDAwMDAwMDB9.invalid"
    sessionStorage.setItem("access_token", expiredToken)
    this.log("Set expired token. Next API call should trigger refresh.")
  }

  /**
   * Test automatic token refresh with a real API call
   * This simulates what happens when your access token expires during normal use
   */
  async testAutoRefresh() {
    this.log("🔄 Testing automatic token refresh with real API call...")
    this.log("")

    // Step 1: Check current tokens
    this.log("Step 1: Checking current token status...")
    const accessToken = sessionStorage.getItem("access_token")
    const refreshToken = sessionStorage.getItem("refresh_token")

    if (!accessToken || !refreshToken) {
      this.error("❌ No tokens found. Please login first.")
      return { success: false, reason: "no_tokens" }
    }

    try {
      const payload = this.decodeJWT(accessToken)
      if (payload.exp) {
        const expiryDate = new Date(payload.exp * 1000)
        const now = new Date()
        const minutesUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / 60000)
        this.log(`Current access token expires in ${minutesUntilExpiry} minutes`)
      }
    } catch (error) {
      this.error("Could not decode access token")
    }

    this.log("")

    // Step 2: Expire the access token
    this.log("Step 2: Setting access token to expired...")
    const expiredToken =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJjbG91ZHNwcnktYXV0aC1zZXJ2aWNlIiwiYXVkIjoiY2xvdWRzcHJ5LWNsaWVudHMiLCJzdWIiOiJ0ZXN0IiwiZXhwIjoxNjAwMDAwMDAwfQ.fake"
    sessionStorage.setItem("access_token", expiredToken)
    this.log("✅ Access token set to expired")
    this.log("")

    // Step 3: Make a real API call - should trigger auto-refresh
    this.log("Step 3: Making API request with expired token...")
    this.log("Expected behavior:")
    this.log("  1. Request fails with 401")
    this.log("  2. Interceptor catches error")
    this.log("  3. Automatically calls refresh endpoint")
    this.log("  4. Gets new access token")
    this.log("  5. Retries original request")
    this.log("  6. Returns successful response")
    this.log("")
    this.log("Watch console for [API] logs...")
    this.log("")

    try {
      // Import api client dynamically
      const apiModule = await import("@/api/http-client")
      const api = apiModule.default

      // Make a simple API call - this should trigger automatic refresh
      this.log("Making GET request to /health endpoint...")
      const response = await api.get("/health")

      this.log("")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.log("🎉 AUTO-REFRESH TEST PASSED!")
      this.log("")
      this.log("✅ API request succeeded after automatic token refresh")
      this.log("✅ Your system automatically handled the expired token")
      this.log("✅ Original request was retried and succeeded")
      this.log("")
      this.log("Response status:", response.status)
      this.log("Response data:", response.data)
      this.log("")

      // Check if token was updated
      const newAccessToken = sessionStorage.getItem("access_token")
      if (newAccessToken !== expiredToken) {
        this.log("✅ Access token was automatically updated")
      }

      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")

      return { success: true, response: response.data }
    } catch (error: any) {
      this.log("")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.error("❌ AUTO-REFRESH TEST FAILED")
      this.log("")
      this.error("Error:", error.message)
      if (error.response) {
        this.error("Response status:", error.response.status)
        this.error("Response data:", error.response.data)
      }
      this.log("")
      this.log("Possible reasons:")
      this.log("  • Refresh token is expired (login again)")
      this.log("  • Backend is not running")
      this.log("  • Network error")
      this.log("  • CORS issue")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")

      return { success: false, error: error.message }
    }
  }

  /**
   * Complete health check - verifies everything is working
   */
  async healthCheck() {
    this.log("🏥 Running complete token health check...")
    this.log("")

    // Step 1: Check tokens exist
    this.log("Step 1: Checking token existence...")
    const accessToken = sessionStorage.getItem("access_token")
    const refreshToken = sessionStorage.getItem("refresh_token")

    if (!accessToken || !refreshToken) {
      this.error("❌ FAILED: Missing tokens")
      this.log("   → Access token:", accessToken ? "✅ Present" : "❌ Missing")
      this.log("   → Refresh token:", refreshToken ? "✅ Present" : "❌ Missing")
      return { healthy: false, reason: "missing_tokens" }
    }

    this.log("✅ Both tokens present")
    this.log("")

    // Step 2: Check token expiry
    this.log("Step 2: Checking token expiry...")
    try {
      const payload = this.decodeJWT(accessToken)
      if (payload.exp) {
        const expiryDate = new Date(payload.exp * 1000)
        const now = new Date()
        const timeUntilExpiry = expiryDate.getTime() - now.getTime()
        const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000)

        if (timeUntilExpiry > 0) {
          this.log(`✅ Access token is valid (expires in ${minutesUntilExpiry} minutes)`)
        } else {
          this.log("⚠️  Access token is expired (will be auto-refreshed on next request)")
        }
      }
    } catch (error) {
      this.error("⚠️  Could not decode access token")
    }
    this.log("")

    // Step 3: Test refresh endpoint
    this.log("Step 3: Testing refresh endpoint...")
    const result = await this.testRefreshEndpoint(true)

    this.log("")

    if (result) {
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.log("🎉 HEALTH CHECK PASSED")
      this.log("   Your token refresh system is working correctly!")
      this.log("   You can now use the app normally.")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      return { healthy: true, result }
    } else {
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.error("❌ HEALTH CHECK FAILED")
      this.log("   Refresh endpoint is not working.")
      this.log("   Check the error details above.")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      return { healthy: false, reason: "refresh_failed" }
    }
  }
}

export const tokenDiagnostics = new TokenDiagnostics()

// Expose globally for easy debugging in console
if (typeof window !== "undefined") {
  ;(window as any).tokenDiagnostics = tokenDiagnostics
}
